import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { Camera, Eye, Heart, ChatCircle, Drop, Barbell, Scales, MagnifyingGlass } from '@phosphor-icons/react';
import { Md } from '@/components/Md';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Hoje' },
  { value: 'week', label: 'Esta semana' },
  { value: 'month', label: 'Este mês' },
  { value: 'custom', label: 'Personalizado' },
];
const TYPE_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'alimentar', label: 'Alimentar' },
  { value: 'agua', label: 'Água' },
  { value: 'atividade', label: 'Atividade' },
  { value: 'peso', label: 'Peso' },
];
const VALIDATION_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'sim', label: 'Validados' },
  { value: 'nao', label: 'Não validados' },
];
const TYPE_ICONS = { alimentar: Camera, agua: Drop, atividade: Barbell, peso: Scales };
const TYPE_COLORS = { alimentar: 'bg-brand-green/10 text-brand-green', agua: 'bg-blue-50 text-blue-500', atividade: 'bg-purple-50 text-purple-500', peso: 'bg-amber-50 text-amber-600' };

function DiaryDetails({ item }) {
  if (item._record_type !== 'alimentar') return null;
  const hasFome = item.hunger_before != null && item.hunger_before !== '';
  const hasSac = item.satiety_after != null && item.satiety_after !== '';
  const syms = Array.isArray(item.symptoms) ? item.symptoms : [];
  const note = item.note;
  if (!hasFome && !hasSac && !note && syms.length === 0) return null;
  return (
    <div className="mt-1.5 space-y-1" data-testid="diary-card-details">
      {(hasFome || hasSac) && (
        <div className="flex gap-2 flex-wrap">
          {hasFome && <span className="text-[9px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-full" data-testid="diary-hunger">Fome antes: {item.hunger_before}/5</span>}
          {hasSac && <span className="text-[9px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full" data-testid="diary-satiety">Saciedade: {item.satiety_after}/5</span>}
        </div>
      )}
      {syms.length > 0 && (
        <div className="flex gap-1 flex-wrap" data-testid="diary-symptoms">
          {syms.map(s => <span key={s} className="text-[9px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded-full">{s.replace(/_/g, ' ')}</span>)}
        </div>
      )}
      {note && <p className="text-[10px] text-brand-muted italic" data-testid="diary-patient-note">"{note}"</p>}
    </div>
  );
}

function RecordCard({ item, onLike, onComment }) {
  const [liked, setLiked] = useState(!!item.nutri_liked);
  const [comment, setComment] = useState(item.nutri_comment || '');
  const [showComment, setShowComment] = useState(!!item.nutri_comment);
  const Icon = TYPE_ICONS[item._record_type] || Camera;

  const toggleLike = async () => { const v = !liked; setLiked(v); await onLike(item, v); };
  const saveComment = async () => { await onComment(item, comment); };

  const getDescription = () => {
    switch (item._record_type) {
      case 'alimentar': return item.description || item.meal_type || '';
      case 'agua': return `${item.glasses || 0} copos de água`;
      case 'atividade': return `${item.type || ''} - ${item.duration_min || 0} min`;
      case 'peso': return `${item.peso || '?'} kg${item.gordura_pct ? ` (${item.gordura_pct}% gordura)` : ''}`;
      default: return '';
    }
  };

  return (
    <div className={`bg-white border rounded-2xl p-3 ${item._validated ? 'border-brand-green/30' : 'border-brand-border'}`} data-testid={`record-${item.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${TYPE_COLORS[item._record_type] || ''}`}>
          <Icon size={16} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-brand-muted">{fmtDatePt(item.date)}</span>
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${TYPE_COLORS[item._record_type] || 'bg-brand-bg text-brand-muted'}`}>{item._record_type}</span>
            {item._validated && <span className="text-[8px] bg-brand-green/10 text-brand-green px-1.5 py-0.5 rounded-full">Validado</span>}
          </div>
          <Md as="p" className="text-xs text-brand-text mt-0.5">{getDescription()}</Md>
          <DiaryDetails item={item} />
          {item.nutri_comment && (
            <div className="mt-1.5 bg-brand-green/5 border-l-2 border-brand-green rounded px-2 py-1" data-testid="diary-nutri-note">
              <span className="text-[9px] text-brand-green font-semibold uppercase tracking-wide">Nutri</span>
              <p className="text-[10px] text-brand-green mt-0.5">{item.nutri_comment}</p>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-2 pt-2 border-t border-brand-border/30">
        <button onClick={toggleLike} className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${liked ? 'bg-brand-gold/15 text-brand-gold-hover border border-brand-gold/30' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>
          <Heart size={12} weight={liked ? 'fill' : 'regular'} /> Gosto
        </button>
        <button onClick={() => setShowComment(!showComment)} className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${comment ? 'bg-brand-green/10 text-brand-green border border-brand-green/20' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>
          <ChatCircle size={12} /> Comentar
        </button>
      </div>
      {showComment && (
        <div className="mt-2">
          <Input value={comment} onChange={e => setComment(e.target.value)} onBlur={saveComment} onKeyDown={e => { if (e.key === 'Enter') saveComment(); }} placeholder="Escreve um comentário..." className="rounded-xl border-brand-border text-xs" />
        </div>
      )}
    </div>
  );
}

export default function AdminDiariosPage() {
  useRequireAdmin();
  const [entries, setEntries] = useState([]);
  const [period, setPeriod] = useState('week');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [recordType, setRecordType] = useState('');
  const [validated, setValidated] = useState('');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const getDateRange = () => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    if (period === 'today') return { from: today, to: today };
    if (period === 'week') { const d = new Date(now - 7 * 86400000); return { from: d.toISOString().split('T')[0], to: today }; }
    if (period === 'month') { const d = new Date(now.getFullYear(), now.getMonth(), 1); return { from: d.toISOString().split('T')[0], to: today }; }
    return { from: dateFrom || today, to: dateTo || today };
  };

  const fetch = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/diarios`);
      setEntries(data);
      await axios.put(`${API}/api/admin/nav-badges/mark-seen`, { type: 'diarios' });
    } catch {}
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const handleLike = async (item, liked) => {
    const type = item._record_type;
    try {
      if (type === 'alimentar') await axios.put(`${API}/api/diary/${item.id}/nutri-feedback`, { liked });
      else if (type === 'agua') await axios.put(`${API}/api/water-logs/${item.date}/nutri-feedback`, { patient_id: item.user_id, liked });
      else if (type === 'atividade') await axios.put(`${API}/api/activities/${item.id}/nutri-feedback`, { liked });
      else if (type === 'peso') await axios.put(`${API}/api/weight-records/${item.id}/nutri-feedback`, { liked });
    } catch {}
  };
  const handleComment = async (item, comment) => {
    const type = item._record_type;
    try {
      if (type === 'alimentar') await axios.put(`${API}/api/diary/${item.id}/nutri-feedback`, { comment });
      else if (type === 'agua') await axios.put(`${API}/api/water-logs/${item.date}/nutri-feedback`, { patient_id: item.user_id, comment });
      else if (type === 'atividade') await axios.put(`${API}/api/activities/${item.id}/nutri-feedback`, { comment });
      else if (type === 'peso') await axios.put(`${API}/api/weight-records/${item.id}/nutri-feedback`, { comment });
      toast.success('Comentário guardado');
    } catch {}
  };

  // Enrich diary entries with _record_type for unified display
  const enriched = entries.map(e => ({ ...e, _record_type: 'alimentar', _validated: !!(e.nutri_liked || e.nutri_comment) }));

  // Filter
  const { from, to } = getDateRange();
  const filtered = enriched.filter(item => {
    if (item.date < from || item.date > to) return false;
    if (recordType && item._record_type !== recordType) return false;
    if (validated === 'sim' && !item._validated) return false;
    if (validated === 'nao' && item._validated) return false;
    if (search && !(item.patient_name || '').toLowerCase().includes(search.toLowerCase()) && !(item.description || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="fade-in" data-testid="admin-diarios-page">
      <div className="mb-5">
        <h2 className="font-heading font-medium text-xl text-brand-text">Diários Alimentares</h2>
        <p className="text-xs text-brand-muted mt-0.5">Registos recentes de todas as clientes</p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4 space-y-3">
        <div className="flex gap-1.5 flex-wrap">
          {PERIOD_OPTIONS.map(p => (
            <button key={p.value} onClick={() => setPeriod(p.value)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${period === p.value ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{p.label}</button>
          ))}
        </div>
        {period === 'custom' && (
          <div className="flex gap-2">
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="rounded-xl border-brand-border text-xs" />
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="rounded-xl border-brand-border text-xs" />
          </div>
        )}
        <div className="flex gap-2 flex-wrap">
          <div className="flex gap-1">
            {TYPE_OPTIONS.map(t => (
              <button key={t.value} onClick={() => setRecordType(t.value)} className={`px-2.5 py-1 rounded-full text-[10px] font-medium ${recordType === t.value ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{t.label}</button>
            ))}
          </div>
          <div className="flex gap-1">
            {VALIDATION_OPTIONS.map(v => (
              <button key={v.value} onClick={() => setValidated(v.value)} className={`px-2.5 py-1 rounded-full text-[10px] font-medium ${validated === v.value ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{v.label}</button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[120px]">
            <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-brand-muted" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pesquisar..." className="pl-7 rounded-xl border-brand-border text-xs h-7" />
          </div>
        </div>
      </div>

      {/* Results */}
      <p className="text-[10px] text-brand-muted mb-2">{filtered.length} registo{filtered.length !== 1 ? 's' : ''}</p>
      {filtered.length === 0 ? <p className="text-center text-brand-muted py-8">Sem registos para os filtros seleccionados.</p> : (
        <div className="space-y-2">
          {filtered.map(item => {
            const inner = (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-brand-text">{item.patient_name || '?'}</span>
                </div>
                <RecordCard item={item} onLike={handleLike} onComment={handleComment} />
              </>
            );
            return item.user_id ? (
              <Link key={item.id} to={`/admin/pacientes/${item.user_id}`} className="block no-underline text-inherit" data-testid={`diario-row-${item.id}`}>{inner}</Link>
            ) : (
              <div key={item.id} data-testid={`diario-row-${item.id}`}>{inner}</div>
            );
          })}
        </div>
      )}
    </div>
  );
}
